<template>
  <v-dialog
    v-model="open"
    max-width="540"
    persistent
    scrim="rgba(20, 35, 45, 0.55)"
    transition="fade-transition"
    :data-testid="`onboarding-tour-${tourId}`"
  >
    <v-card class="onboarding-card">
      <v-btn
        icon
        variant="text"
        size="small"
        class="onboarding-close"
        :aria-label="t('onboarding.skip')"
        :data-testid="`onboarding-close-${tourId}`"
        @click="handleSkip"
      >
        <v-icon icon="mdi-close" />
      </v-btn>

      <div class="onboarding-icon-tile" :class="`onboarding-tone-${currentStep.tone || 'primary'}`">
        <v-icon :icon="currentStep.icon" size="56" />
      </div>

      <h2 class="onboarding-title">{{ t(currentStep.titleKey) }}</h2>
      <p class="onboarding-body">{{ t(currentStep.bodyKey) }}</p>

      <div class="onboarding-dots" role="tablist" :aria-label="t('onboarding.progressLabel')">
        <span
          v-for="(_, i) in steps"
          :key="i"
          class="onboarding-dot"
          :class="{ active: i === stepIndex }"
          role="tab"
          :aria-selected="i === stepIndex ? 'true' : 'false'"
        />
      </div>

      <div class="onboarding-actions">
        <v-btn
          variant="text"
          size="small"
          class="onboarding-skip-text"
          :data-testid="`onboarding-skip-${tourId}`"
          @click="handleSkip"
        >
          {{ t('onboarding.skip') }}
        </v-btn>
        <v-spacer />
        <v-btn
          v-if="stepIndex > 0"
          variant="text"
          :data-testid="`onboarding-back-${tourId}`"
          @click="goBack"
        >
          {{ t('onboarding.back') }}
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :data-testid="`onboarding-next-${tourId}`"
          @click="goNext"
        >
          {{ isLastStep ? t('onboarding.done') : t('onboarding.next') }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { hasTourBeenSeen, markTourSeen, tourStorageKey } from '@/utils/onboardingState.mjs';

const props = defineProps({
  tourId: { type: String, required: true },
  version: { type: Number, default: 1 },
  // [{ icon: string, titleKey: string, bodyKey: string, tone?: string }, ...]
  steps: { type: Array, required: true },
  // Gate the auto-show — only check the localStorage flag once the host
  // says it's the right moment (e.g. after analysis state has hydrated).
  ready: { type: Boolean, default: true },
  autoShow: { type: Boolean, default: true },
});

const emit = defineEmits(['finish', 'skip']);

const { t } = useI18n();
const stepIndex = ref(0);
const open = ref(false);

const currentStep = computed(() => props.steps[stepIndex.value] || props.steps[0]);
const isLastStep = computed(() => stepIndex.value === props.steps.length - 1);

function maybeOpen() {
  if (!props.autoShow) return;
  if (!props.ready) return;
  if (!props.steps.length) return;
  if (hasTourBeenSeen(props.tourId, props.version)) return;
  stepIndex.value = 0;
  open.value = true;
}

onMounted(maybeOpen);
watch(() => props.ready, (isReady) => { if (isReady) maybeOpen(); });

function goBack() {
  if (stepIndex.value > 0) stepIndex.value -= 1;
}

function goNext() {
  if (isLastStep.value) {
    markTourSeen(props.tourId, props.version);
    open.value = false;
    emit('finish');
  } else {
    stepIndex.value += 1;
  }
}

function handleSkip() {
  markTourSeen(props.tourId, props.version);
  open.value = false;
  emit('skip', { atStep: stepIndex.value });
}

defineExpose({
  storageKey: () => tourStorageKey(props.tourId, props.version),
});
</script>

<style scoped>
.onboarding-card {
  position: relative;
  padding: 32px 32px 20px;
  border-radius: 16px;
  background: #ffffff;
  text-align: center;
}

.onboarding-close {
  position: absolute;
  top: 8px;
  right: 8px;
  color: rgba(20, 35, 45, 0.55);
}

.onboarding-icon-tile {
  width: 84px;
  height: 84px;
  margin: 4px auto 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.onboarding-tone-primary {
  background: linear-gradient(135deg, #eef5f7 0%, #b8d4dd 100%);
  color: #1f4a55;
}

.onboarding-tone-studio {
  background: linear-gradient(135deg, #f1ecfb 0%, #cdbef0 100%);
  color: #4a378a;
}

.onboarding-tone-success {
  background: linear-gradient(135deg, #e8f3e6 0%, #c9e0c4 100%);
  color: #2f6b3a;
}

.onboarding-tone-warm {
  background: linear-gradient(135deg, #fbe9d6 0%, #f1c79b 100%);
  color: #8a4a14;
}

.onboarding-title {
  margin: 0 0 12px;
  font-size: 1.35rem;
  line-height: 1.3;
  font-weight: 700;
  color: #172b33;
}

.onboarding-body {
  margin: 0 auto 24px;
  max-width: 420px;
  color: #4a5d66;
  font-size: 1rem;
  line-height: 1.55;
}

.onboarding-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
}

.onboarding-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(34, 81, 92, 0.22);
  transition: background-color 200ms ease, transform 200ms ease;
}

.onboarding-dot.active {
  background: var(--v-theme-primary, #22515c);
  transform: scale(1.25);
}

.onboarding-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.onboarding-skip-text {
  color: rgba(20, 35, 45, 0.6);
}

@media (max-width: 600px) {
  .onboarding-card {
    padding: 24px 18px 16px;
  }
  .onboarding-title {
    font-size: 1.2rem;
  }
  .onboarding-body {
    font-size: 0.95rem;
  }
}
</style>
